import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { cartRecoveryAPI } from '../../services/api';

/**
 * Cart Recovery — NEW admin tab (Phase 2). Configure the abandoned-cart recovery
 * engine (staged messages, holdout %, discount gating) and read recovery stats
 * incl. the holdout-vs-treatment measured lift. Purely additive.
 */

const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const num = (n) => (n ?? 0).toLocaleString('en-IN');
const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20, boxShadow: '0 4px 10px rgba(0,0,0,0.05)' };
const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 14 });
const kpi = { ...card, textAlign: 'center' };

function monthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { startDate: iso(start), endDate: iso(now) };
}

function CartRecovery() {
  const [range] = useState(monthRange());
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, st] = await Promise.all([cartRecoveryAPI.getSettings(), cartRecoveryAPI.getStats(range)]);
      setCfg(s.data.data);
      setStats(st.data.data);
    } catch (e) { setMsg('Failed to load'); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    try {
      const r = await cartRecoveryAPI.updateSettings(patch);
      setCfg(r.data.data);
      setMsg('Saved');
    } catch (e) { setMsg(e.response?.data?.message || 'Save failed'); }
  };

  const updateStage = (i, key, val) => {
    const stages = [...(cfg.stages || [])];
    stages[i] = { ...stages[i], [key]: val };
    save({ stages });
  };

  if (loading || !cfg) return (<div><Navbar /><div style={{ padding: 24 }}>🔄 Loading…</div></div>);

  const exp = stats?.experiment || {};

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
        <h1 style={{ marginBottom: 4 }}>🛒 Cart Recovery</h1>
        <p style={{ color: '#777', marginTop: 0 }}>Re-engage abandoned carts with a staged sequence. A holdout group measures true lift.</p>
        {msg && <div style={{ ...card, borderColor: '#10B981', color: '#059669', marginBottom: 16 }}>{msg}</div>}

        {/* Master toggle */}
        <div style={{ ...card, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => save({ enabled: e.target.checked })} />
            <b>Recovery Engine {cfg.enabled ? 'ON' : 'OFF'}</b>
          </label>
          <span style={{ fontSize: 12, color: '#999' }}>
            {cfg.enabled ? 'Abandoned carts are being re-engaged.' : 'Off — turn on once the app build that sends carts is live.'}
          </span>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 8 }}>
          <div style={kpi}><div style={{ color: '#777', fontSize: 12 }}>Carts</div><div style={{ fontSize: 22, fontWeight: 700 }}>{num(stats?.totalCarts)}</div></div>
          <div style={kpi}><div style={{ color: '#777', fontSize: 12 }}>Recovered</div><div style={{ fontSize: 22, fontWeight: 700, color: '#059669' }}>{num(stats?.recovered)}</div></div>
          <div style={kpi}><div style={{ color: '#777', fontSize: 12 }}>Recovered Revenue</div><div style={{ fontSize: 22, fontWeight: 700, color: '#059669' }}>{fmt(stats?.recoveredRevenue)}</div></div>
          <div style={kpi}><div style={{ color: '#777', fontSize: 12 }}>Messaged</div><div style={{ fontSize: 22, fontWeight: 700 }}>{num(stats?.messagedCarts)}</div></div>
          <div style={kpi}><div style={{ color: '#777', fontSize: 12 }}>Expired</div><div style={{ fontSize: 22, fontWeight: 700, color: '#EF4444' }}>{num(stats?.expired)}</div></div>
        </div>

        {/* Experiment / lift */}
        <div style={{ ...card, marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Measured Lift (holdout experiment)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <div><div style={{ color: '#888', fontSize: 12 }}>Treatment (messaged)</div><b>{exp.treatmentConversionPct != null ? `${exp.treatmentConversionPct}%` : '—'}</b> <span style={{ fontSize: 11, color: '#999' }}>of {num(exp.treatmentCarts)}</span></div>
            <div><div style={{ color: '#888', fontSize: 12 }}>Control (holdout)</div><b>{exp.controlConversionPct != null ? `${exp.controlConversionPct}%` : '—'}</b> <span style={{ fontSize: 11, color: '#999' }}>of {num(exp.controlCarts)}</span></div>
            <div><div style={{ color: '#888', fontSize: 12 }}>Incremental Lift</div><b style={{ color: (exp.measuredLiftPct || 0) >= 0 ? '#059669' : '#EF4444', fontSize: 18 }}>{exp.measuredLiftPct != null ? `${exp.measuredLiftPct > 0 ? '+' : ''}${exp.measuredLiftPct} pts` : '—'}</b></div>
          </div>
          <p style={{ fontSize: 11, color: '#aaa', marginBottom: 0 }}>Lift = treatment conversion − control conversion. This is the TRUE incremental recovery (orders that wouldn't have happened without messaging).</p>
        </div>

        {/* Config */}
        <div style={{ ...card, marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Configuration</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <label>Holdout % (control group)
              <input type="number" min="0" max="90" defaultValue={cfg.holdoutPercent}
                onBlur={(e) => save({ holdoutPercent: Number(e.target.value) })} style={{ width: '100%' }} />
            </label>
            <label>Recovery coupon code (discount stage)
              <input defaultValue={cfg.recoveryCouponCode}
                onBlur={(e) => save({ recoveryCouponCode: e.target.value })} style={{ width: '100%' }} />
            </label>
            <label>Expire carts after (hours)
              <input type="number" min="1" defaultValue={cfg.expireHours}
                onBlur={(e) => save({ expireHours: Number(e.target.value) })} style={{ width: '100%' }} />
            </label>
            <label>WhatsApp template (MSG91)
              <input defaultValue={cfg.whatsappTemplateName}
                onBlur={(e) => save({ whatsappTemplateName: e.target.value })} style={{ width: '100%' }} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={cfg.discountOnlyForSensitive} onChange={(e) => save({ discountOnlyForSensitive: e.target.checked })} />
              Discount only for discount-sensitive users
            </label>
          </div>
        </div>

        {/* Stages */}
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>Recovery Sequence</h3>
          <p style={{ fontSize: 12, color: '#888', marginTop: 0 }}>Each stage fires when its delay (since last cart activity) elapses. Tip: don't lead with a discount.</p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ textAlign: 'left', padding: 8, fontSize: 13, color: '#555' }}>#</th>
              <th style={{ textAlign: 'left', padding: 8, fontSize: 13, color: '#555' }}>Delay (min)</th>
              <th style={{ textAlign: 'left', padding: 8, fontSize: 13, color: '#555' }}>Channel</th>
              <th style={{ textAlign: 'left', padding: 8, fontSize: 13, color: '#555' }}>Include discount</th>
            </tr></thead>
            <tbody>
              {(cfg.stages || []).map((st, i) => (
                <tr key={i}>
                  <td style={{ padding: 8 }}>{i + 1}</td>
                  <td style={{ padding: 8 }}>
                    <input type="number" min="1" defaultValue={st.delayMinutes} onBlur={(e) => updateStage(i, 'delayMinutes', Number(e.target.value))} style={{ width: 90 }} />
                  </td>
                  <td style={{ padding: 8 }}>
                    <select value={st.channel} onChange={(e) => updateStage(i, 'channel', e.target.value)}>
                      <option value="push">push</option>
                      <option value="whatsapp">whatsapp</option>
                    </select>
                  </td>
                  <td style={{ padding: 8 }}>
                    <input type="checkbox" checked={!!st.withDiscount} onChange={(e) => updateStage(i, 'withDiscount', e.target.checked)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default CartRecovery;
